<template>
  <div class="audit-log-wrapper">
    <!-- Toast Notifications -->
    <div class="toast-container">
      <CToaster :autohide="3000">
        <CToast
          v-for="(info, index) in infoList"
          :key="`toast-${index}-${Date.now()}`"
          :show="true"
          :header="info.header"
          :color="info.color"
        >
          {{ info.message }}
        </CToast>
      </CToaster>
    </div>

    <!-- Main Content -->
    <CRow>
      <CCol>
        <CCard class="main-card">
          <CCardHeader class="professional-header">
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div class="header-title">
                <h5 class="mb-0">
                  <i class="fas fa-clipboard-list me-2"></i>
                  <strong>Audit Logs</strong>
                </h5>
                <small class="text-muted">System activity and security audit trail</small>
              </div>
              <div class="header-actions">
                <CBadge color="info" class="result-counter">
                  {{ totalCount }} {{ totalCount === 1 ? 'Log' : 'Logs' }}
                </CBadge>
              </div>
            </div>
          </CCardHeader>

          <CCardBody>
            <!-- Filters -->
            <CCard class="mb-3">
              <CCardBody>
                <CRow>
                  <CCol sm="12" md="6" lg="3" class="mb-2">
                    <CInput
                      label="User"
                      :value.sync="filters.userName"
                      placeholder="Filter by username"
                      @input="applyFilters"
                    />
                  </CCol>
                  <CCol sm="12" md="6" lg="3" class="mb-2">
                    <CSelect
                      label="Event Type"
                      :value.sync="filters.eventType"
                      :options="eventTypeOptions"
                      placeholder="All Events"
                      @update:value="applyFilters"
                    />
                  </CCol>
                  <CCol sm="12" md="6" lg="3" class="mb-2">
                    <CSelect
                      label="Severity"
                      :value.sync="filters.severity"
                      :options="severityOptions"
                      placeholder="All Severities"
                      @update:value="applyFilters"
                    />
                  </CCol>
                </CRow>
                <CRow>
                  <CCol sm="12" md="6" lg="3" class="mb-2">
                    <CInput
                      label="Start Date"
                      type="date"
                      :value.sync="filters.startDate"
                      @input="applyFilters"
                    />
                  </CCol>
                  <CCol sm="12" md="6" lg="3" class="mb-2">
                    <CInput
                      label="End Date"
                      type="date"
                      :value.sync="filters.endDate"
                      @input="applyFilters"
                    />
                  </CCol>
                  <CCol sm="12" md="6" lg="6" class="mb-2 d-flex align-items-end">
                    <CButton color="secondary" size="sm" @click="clearFilters" class="me-2">
                      <i class="fas fa-times me-1"></i>
                      Clear Filters
                    </CButton>
                    <CButton color="primary" size="sm" @click="refreshTable">
                      <i class="fas fa-sync me-1"></i>
                      Refresh
                    </CButton>
                  </CCol>
                </CRow>
              </CCardBody>
            </CCard>

            <!-- Loading State -->
            <div v-if="loading && items.length === 0" class="loading-state">
              <div class="text-center py-5">
                <CSpinner color="primary" />
                <p class="text-muted mt-3">Loading audit logs...</p>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else-if="!loading && items.length === 0" class="empty-state">
              <div class="text-center py-5">
                <i class="fas fa-clipboard-list fa-3x text-muted mb-3"></i>
                <h5 class="text-muted">No audit logs found</h5>
                <p class="text-muted">Try adjusting your filters</p>
              </div>
            </div>

            <!-- Data View -->
            <div v-else>
              <!-- Desktop Table View -->
              <div class="table-responsive">
                <CDataTable
                  :items="items"
                  :fields="fields"
                  :hover="true"
                  :loading="loading"
                  @row-clicked="viewDetails"
                  class="professional-table"
                >
                  <template #show_index="{ index }">
                    <td class="text-center">
                      <span class="text-muted">#{{ (currentPage - 1) * pageSize + index + 1 }}</span>
                    </td>
                  </template>

                  <template #timestamp="{ item }">
                    <td>
                      <div>{{ formatDateTime(item.timestamp) }}</div>
                      <small class="text-muted">{{ formatTimeAgo(item.timestamp) }}</small>
                    </td>
                  </template>

                  <template #userName="{ item }">
                    <td>
                      <div v-if="item.userName">
                        <i class="fas fa-user me-1 text-muted"></i>
                        {{ item.userName }}
                      </div>
                      <span v-else class="text-muted">System</span>
                    </td>
                  </template>

                  <template #eventType="{ item }">
                    <td>
                      <CBadge :color="getEventTypeBadgeColor(item.eventType)">
                        {{ getEventTypeText(item.eventType) }}
                      </CBadge>
                    </td>
                  </template>

                  <template #severity="{ item }">
                    <td class="text-center">
                      <CBadge :color="getSeverityBadgeColor(item.severity)" class="px-2 py-1">
                        <i :class="getSeverityIcon(item.severity)" class="me-1"></i>
                        {{ item.severity }}
                      </CBadge>
                    </td>
                  </template>

                  <template #action="{ item }">
                    <td>
                      <strong>{{ item.action }}</strong>
                      <div v-if="item.description" class="text-muted small">
                        {{ truncate(item.description, 50) }}
                      </div>
                    </td>
                  </template>

                  <template #isSuccess="{ item }">
                    <td class="text-center">
                      <i v-if="item.isSuccess" class="fas fa-check-circle text-success"></i>
                      <i v-else class="fas fa-times-circle text-danger"></i>
                    </td>
                  </template>

                  <template #show_details="{ item }">
                    <td class="text-end">
                      <CButton
                        color="info"
                        variant="outline"
                        size="sm"
                        @click="viewDetails(item)"
                      >
                        <i class="fas fa-eye me-1"></i>
                        View
                      </CButton>
                    </td>
                  </template>
                </CDataTable>
              </div>

              <!-- Pagination -->
              <CPagination
                v-if="totalPages > 1"
                :activePage.sync="currentPage"
                :pages="totalPages"
                @update:activePage="changePage"
                align="center"
                class="mt-3"
              />
            </div>
          </CCardBody>

          <!-- Footer -->
          <CCardFooter class="card-footer">
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div class="footer-info">
                <small class="text-muted">
                  Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalCount) }} of {{ totalCount }} logs
                </small>
              </div>
              <div class="footer-actions">
                <CButton
                  color="success"
                  variant="outline"
                  size="sm"
                  @click="exportCSV"
                >
                  <i class="fas fa-download me-2"></i>
                  Export CSV
                </CButton>
                <CButton
                  color="warning"
                  variant="outline"
                  size="sm"
                  class="ms-2"
                  @click="showCleanupModal = true"
                  v-permission="{ subModule: 'AUDIT_LOGS', action: 'CanDelete' }"
                >
                  <i class="fas fa-trash me-2"></i>
                  Cleanup Old Logs
                </CButton>
              </div>
            </div>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>

    <!-- Cleanup Modal -->
    <CModal
      :show.sync="showCleanupModal"
      color="warning"
      size="lg"
    >
      <template #header>
        <h5 class="modal-title">
          <i class="fas fa-trash me-2"></i>
          Cleanup Old Audit Logs
        </h5>
      </template>

      <CAlert color="warning" :show="true">
        <i class="fas fa-exclamation-triangle me-2"></i>
        <strong>Warning:</strong> This action will permanently delete audit logs older than the specified retention period. This cannot be undone.
      </CAlert>

      <CRow>
        <CCol sm="12">
          <CInput
            label="Retention Period (Days)"
            type="number"
            :value.sync="cleanupDays"
            placeholder="Enter days (minimum 30)"
            :min="30"
            help-text="Logs older than this many days will be deleted. Minimum: 30 days"
          />
        </CCol>
      </CRow>

      <template #footer>
        <CButton color="secondary" @click="showCleanupModal = false">
          Cancel
        </CButton>
        <CButton
          color="danger"
          @click="performCleanup"
          :disabled="cleanupDays < 30"
        >
          <i class="fas fa-trash me-2"></i>
          Delete Old Logs
        </CButton>
      </template>
    </CModal>

    <!-- Detail Modal -->
    <CModal
      :show.sync="showDetailModal"
      size="xl"
    >
      <template #header>
        <h5 class="modal-title">
          <i class="fas fa-info-circle me-2"></i>
          Audit Log Details
        </h5>
      </template>

      <div v-if="selectedItem">
        <CRow>
          <CCol sm="12" md="6" class="mb-3">
            <strong>Timestamp:</strong><br />
            {{ formatDateTime(selectedItem.timestamp) }}
          </CCol>
          <CCol sm="12" md="6" class="mb-3">
            <strong>User:</strong><br />
            {{ selectedItem.userName || 'System' }}
          </CCol>
          <CCol sm="12" md="6" class="mb-3">
            <strong>Event Type:</strong><br />
            <CBadge :color="getEventTypeBadgeColor(selectedItem.eventType)">
              {{ selectedItem.eventType }}
            </CBadge>
          </CCol>
          <CCol sm="12" md="6" class="mb-3">
            <strong>Severity:</strong><br />
            <CBadge :color="getSeverityBadgeColor(selectedItem.severity)">
              {{ selectedItem.severity }}
            </CBadge>
          </CCol>
          <CCol sm="12" class="mb-3">
            <strong>Action:</strong><br />
            {{ selectedItem.action }}
          </CCol>
          <CCol sm="12" class="mb-3">
            <strong>Description:</strong><br />
            {{ selectedItem.description || 'N/A' }}
          </CCol>
          <CCol sm="12" md="6" class="mb-3" v-if="selectedItem.entityType">
            <strong>Entity Type:</strong><br />
            {{ selectedItem.entityType }}
          </CCol>
          <CCol sm="12" md="6" class="mb-3" v-if="selectedItem.entityName">
            <strong>Entity Name:</strong><br />
            {{ selectedItem.entityName }}
          </CCol>
          <CCol sm="12" md="6" class="mb-3">
            <strong>IP Address:</strong><br />
            {{ selectedItem.ipAddress || 'N/A' }}
          </CCol>
          <CCol sm="12" md="6" class="mb-3">
            <strong>Status:</strong><br />
            <CBadge :color="selectedItem.isSuccess ? 'success' : 'danger'">
              {{ selectedItem.isSuccess ? 'Success' : 'Failed' }}
            </CBadge>
          </CCol>
          <CCol sm="12" class="mb-3" v-if="selectedItem.errorMessage">
            <strong>Error Message:</strong><br />
            <CAlert color="danger" :show="true">
              {{ selectedItem.errorMessage }}
            </CAlert>
          </CCol>
        </CRow>

        <!-- Enhanced Change History with Field Highlighting -->
        <div v-if="selectedItem.oldValues || selectedItem.newValues" class="mt-4">
          <hr />

          <!-- Header with Toggle -->
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0">
              <i class="fas fa-exchange-alt me-2"></i>
              Change History
              <CBadge v-if="typeof selectedItem.oldValues === 'object' && typeof selectedItem.newValues === 'object'"
                      color="info" class="ms-2">
                {{ Object.keys(compareValues(selectedItem.oldValues, selectedItem.newValues))
                    .filter(k => compareValues(selectedItem.oldValues, selectedItem.newValues)[k].type !== 'unchanged').length }}
                changed
              </CBadge>
            </h6>
            <div class="form-check form-switch" v-if="typeof selectedItem.oldValues === 'object' && typeof selectedItem.newValues === 'object'">
              <input
                class="form-check-input"
                type="checkbox"
                id="showOnlyChangedToggle"
                v-model="showOnlyChangedFields"
              >
              <label class="form-check-label" for="showOnlyChangedToggle">
                Show only changed fields
              </label>
            </div>
          </div>

          <!-- Fallback for String Values (legacy format) -->
          <div v-if="typeof selectedItem.oldValues === 'string' || typeof selectedItem.newValues === 'string'">
            <CRow>
              <CCol sm="12" md="6" v-if="selectedItem.oldValues">
                <CCard color="light" class="mb-3">
                  <CCardHeader class="bg-danger text-white">
                    <strong><i class="fas fa-arrow-left me-2"></i>Before Changes</strong>
                  </CCardHeader>
                  <CCardBody>
                    <pre class="mb-0">{{ formatJSON(selectedItem.oldValues) }}</pre>
                  </CCardBody>
                </CCard>
              </CCol>
              <CCol sm="12" md="6" v-if="selectedItem.newValues">
                <CCard color="light" class="mb-3">
                  <CCardHeader class="bg-success text-white">
                    <strong><i class="fas fa-arrow-right me-2"></i>After Changes</strong>
                  </CCardHeader>
                  <CCardBody>
                    <pre class="mb-0">{{ formatJSON(selectedItem.newValues) }}</pre>
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>
          </div>

          <!-- Enhanced Object Comparison with Highlighting -->
          <div v-else>
            <CCard class="comparison-card">
              <CCardBody class="p-0">
                <table class="table table-sm table-hover mb-0 comparison-table">
                  <thead>
                    <tr>
                      <th style="width: 5%"></th>
                      <th style="width: 25%">Field</th>
                      <th style="width: 35%">Before</th>
                      <th style="width: 35%">After</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="(comparison, key) in compareValues(selectedItem.oldValues, selectedItem.newValues)">
                      <tr
                        v-if="!showOnlyChangedFields || comparison.type !== 'unchanged'"
                        :key="`field-${key}`"
                        :class="getChangeClass(comparison.type)"
                      >
                        <!-- Change Icon -->
                        <td class="text-center change-icon-cell">
                          <i :class="getChangeIcon(comparison.type)" v-if="comparison.type !== 'unchanged'"></i>
                        </td>

                        <!-- Field Name -->
                        <td class="field-name-cell">
                          <strong>{{ key }}</strong>
                        </td>

                        <!-- Old Value (Before) -->
                        <td class="value-cell">
                          <!-- Array Display -->
                          <template v-if="isArray(comparison.oldValue)">
                            <div class="array-preview">
                              <i class="fas fa-list me-1"></i>
                              Array ({{ comparison.oldValue.length }} items)
                            </div>
                          </template>
                          <!-- Regular Value -->
                          <template v-else>
                            <span v-if="comparison.oldValue !== null" class="value-text">
                              {{ formatDisplayValue(comparison.oldValue, key) }}
                            </span>
                            <span v-else class="text-muted">—</span>
                          </template>
                        </td>

                        <!-- New Value (After) -->
                        <td class="value-cell">
                          <!-- Array Display -->
                          <template v-if="isArray(comparison.newValue)">
                            <div class="array-preview">
                              <i class="fas fa-list me-1"></i>
                              Array ({{ comparison.newValue.length }} items)
                            </div>
                          </template>
                          <!-- Regular Value -->
                          <template v-else>
                            <span v-if="comparison.newValue !== null" class="value-text">
                              {{ formatDisplayValue(comparison.newValue, key) }}
                            </span>
                            <span v-else class="text-muted">—</span>
                          </template>
                        </td>
                      </tr>

                      <!-- Array Detailed Comparison (expandable) -->
                      <tr
                        v-if="(isArray(comparison.oldValue) || isArray(comparison.newValue)) && comparison.type !== 'unchanged' && (!showOnlyChangedFields || comparison.type !== 'unchanged')"
                        :key="`array-detail-${key}`"
                        class="array-detail-row"
                      >
                        <td colspan="4" class="p-3">
                          <div class="array-comparison-detail">
                            <strong class="text-muted d-block mb-2">
                              <i class="fas fa-list me-1"></i>{{ key }} - Item Comparison
                            </strong>

                            <!-- Array Comparison Results -->
                            <div v-if="comparison.oldValue && comparison.newValue" class="array-diff">
                              <template v-for="(arrayComp, idx) in [compareArrays(comparison.oldValue, comparison.newValue)]">
                                <!-- Added Items -->
                                <div v-if="arrayComp.added.length > 0" :key="`added-${idx}`" class="mb-2">
                                  <CBadge color="success" class="me-2">
                                    <i class="fas fa-plus me-1"></i>{{ arrayComp.added.length }} Added
                                  </CBadge>
                                  <div class="array-items">
                                    <div v-for="(addedItem, aIdx) in arrayComp.added" :key="`add-${aIdx}`"
                                         class="array-item field-added p-2 mb-1">
                                      <small v-if="isPlainObject(addedItem.item)">
                                        <span v-for="(val, k) in addedItem.item" :key="`add-field-${k}`" class="me-2">
                                          <strong>{{ k }}:</strong> {{ formatDisplayValue(val) }}
                                        </span>
                                      </small>
                                      <small v-else>{{ formatDisplayValue(addedItem.item) }}</small>
                                    </div>
                                  </div>
                                </div>

                                <!-- Removed Items -->
                                <div v-if="arrayComp.removed.length > 0" :key="`removed-${idx}`" class="mb-2">
                                  <CBadge color="danger" class="me-2">
                                    <i class="fas fa-minus me-1"></i>{{ arrayComp.removed.length }} Removed
                                  </CBadge>
                                  <div class="array-items">
                                    <div v-for="(removedItem, rIdx) in arrayComp.removed" :key="`rem-${rIdx}`"
                                         class="array-item field-removed p-2 mb-1">
                                      <small v-if="isPlainObject(removedItem.item)">
                                        <span v-for="(val, k) in removedItem.item" :key="`rem-field-${k}`" class="me-2">
                                          <strong>{{ k }}:</strong> {{ formatDisplayValue(val) }}
                                        </span>
                                      </small>
                                      <small v-else>{{ formatDisplayValue(removedItem.item) }}</small>
                                    </div>
                                  </div>
                                </div>

                                <!-- Modified Items -->
                                <div v-if="arrayComp.modified.length > 0" :key="`modified-${idx}`" class="mb-2">
                                  <CBadge color="warning" class="me-2">
                                    <i class="fas fa-edit me-1"></i>{{ arrayComp.modified.length }} Modified
                                  </CBadge>
                                  <div class="array-items">
                                    <div v-for="(modItem, mIdx) in arrayComp.modified" :key="`mod-${mIdx}`"
                                         class="array-item field-modified p-2 mb-1">
                                      <small v-if="isPlainObject(modItem.oldItem)">
                                        <div class="d-flex justify-content-between">
                                          <span class="text-decoration-line-through text-muted me-2">
                                            <span v-for="(val, k) in modItem.oldItem" :key="`old-${k}`" class="me-2">
                                              <strong>{{ k }}:</strong> {{ formatDisplayValue(val) }}
                                            </span>
                                          </span>
                                          <span class="fas fa-arrow-right mx-2"></span>
                                          <span>
                                            <span v-for="(val, k) in modItem.newItem" :key="`new-${k}`" class="me-2">
                                              <strong>{{ k }}:</strong> {{ formatDisplayValue(val) }}
                                            </span>
                                          </span>
                                        </div>
                                      </small>
                                      <small v-else>
                                        <span class="text-decoration-line-through text-muted">{{ formatDisplayValue(modItem.oldItem) }}</span>
                                        <i class="fas fa-arrow-right mx-2"></i>
                                        {{ formatDisplayValue(modItem.newItem) }}
                                      </small>
                                    </div>
                                  </div>
                                </div>
                              </template>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </CCardBody>
            </CCard>
          </div>
        </div>
      </div>

      <template #footer>
        <CButton color="secondary" @click="showDetailModal = false">
          Close
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import AuditLogApi from "../../lib/auditLogApi";
import { formatMalaysiaDateTime } from "@/utils/dateUtil";

export default {
  name: "AuditLogList",
  data() {
    return {
      loading: true,
      items: [],
      infoList: [],
      currentPage: 1,
      pageSize: 50,
      totalCount: 0,
      totalPages: 0,
      showDetailModal: false,
      showCleanupModal: false,
      selectedItem: null,
      cleanupDays: 90,
      showOnlyChangedFields: true, // Toggle to show only changed fields
      filters: {
        userName: null,
        eventType: null,
        severity: null,
        startDate: null,
        endDate: null,
      },
      fields: [
        {
          key: "show_index",
          label: "#",
          _style: "width:60px",
          sorter: false,
          filter: false,
        },
        {
          key: "timestamp",
          label: "Time",
          _style: "min-width:180px;"
        },
        {
          key: "userName",
          label: "User",
          _style: "min-width:120px;"
        },
        {
          key: "eventType",
          label: "Event",
          _style: "min-width:140px;"
        },
        {
          key: "severity",
          label: "Severity",
          _style: "width:100px;text-align:center;"
        },
        {
          key: "action",
          label: "Action",
          _style: "min-width:200px;"
        },
        {
          key: "isSuccess",
          label: "Status",
          _style: "width:80px;text-align:center;"
        },
        {
          key: "show_details",
          label: "",
          _style: "width:100px",
          sorter: false,
          filter: false,
        },
      ],
      eventTypeOptions: [
        { value: null, label: 'All Events' },
        { value: 'Login', label: 'Login' },
        { value: 'Logout', label: 'Logout' },
        { value: 'LoginFailed', label: 'Login Failed' },
        { value: 'EntityCreated', label: 'Created' },
        { value: 'EntityUpdated', label: 'Updated' },
        { value: 'EntityDeleted', label: 'Deleted' },
        { value: 'RoleAssigned', label: 'Role Assigned' },
        { value: 'RoleRevoked', label: 'Role Revoked' },
      ],
      severityOptions: [
        { value: null, label: 'All Severities' },
        { value: 'Info', label: 'Info' },
        { value: 'Low', label: 'Low' },
        { value: 'Medium', label: 'Medium' },
        { value: 'High', label: 'High' },
        { value: 'Critical', label: 'Critical' },
      ],
      api: new AuditLogApi(),
    };
  },
  mounted() {
    this.refreshTable();
  },
  methods: {
    toast(header, message, color) {
      this.infoList.push({
        header: header,
        message: message,
        color: color,
      });
    },

    refreshTable() {
      this.loading = true;
      const params = {
        page: this.currentPage,
        pageSize: this.pageSize,
        ...this.filters
      };

      this.api
        .getList(params)
        .then((response) => {
          this.items = response.result?.data || [];
          this.totalCount = response.result?.totalCount || 0;
          this.totalPages = response.result?.totalPages || 0;
          this.loading = false;
        })
        .catch((error) => {
          this.toast("Error", "Failed to load audit logs", "danger");
          this.loading = false;
        });
    },

    changePage(page) {
      this.currentPage = page;
      this.refreshTable();
    },

    applyFilters() {
      this.currentPage = 1;
      this.refreshTable();
    },

    clearFilters() {
      this.filters = {
        userName: null,
        eventType: null,
        severity: null,
        startDate: null,
        endDate: null,
      };
      this.applyFilters();
    },

    viewDetails(item) {
      // Fetch full details including oldValues and newValues
      this.api
        .get(item.id)
        .then((response) => {
          this.selectedItem = response.result;

          // Parse JSON strings if needed
          if (this.selectedItem.oldValues && typeof this.selectedItem.oldValues === 'string') {
            try {
              this.selectedItem.oldValues = JSON.parse(this.selectedItem.oldValues);
            } catch (e) {
              // Keep as string if parsing fails
            }
          }
          if (this.selectedItem.newValues && typeof this.selectedItem.newValues === 'string') {
            try {
              this.selectedItem.newValues = JSON.parse(this.selectedItem.newValues);
            } catch (e) {
              // Keep as string if parsing fails
            }
          }

          this.showDetailModal = true;
        })
        .catch((error) => {
          this.toast("Error", "Failed to load audit log details", "danger");
        });
    },

    performCleanup() {
      if (this.cleanupDays < 30) {
        this.toast("Error", "Minimum retention period is 30 days", "danger");
        return;
      }

      this.api
        .cleanup(this.cleanupDays)
        .then((response) => {
          const deletedCount = response.result?.deletedCount || 0;
          this.toast("Success", `Deleted ${deletedCount} old audit logs`, "success");
          this.showCleanupModal = false;
          this.refreshTable();
        })
        .catch((error) => {
          this.toast("Error", "Failed to cleanup audit logs", "danger");
        });
    },

    exportCSV() {
      const csvData = this.items.map((item, index) => ({
        '#': (this.currentPage - 1) * this.pageSize + index + 1,
        'Timestamp': this.formatDateTime(item.timestamp),
        'User': item.userName || 'System',
        'Event Type': item.eventType,
        'Severity': item.severity,
        'Action': item.action,
        'Description': item.description || '',
        'Status': item.isSuccess ? 'Success' : 'Failed',
        'IP Address': item.ipAddress || '',
      }));

      const headers = Object.keys(csvData[0]);
      const csvContent = [
        headers.join(','),
        ...csvData.map(row => headers.map(header => `"${row[header]}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.toast("Success", "CSV exported successfully", "success");
    },

    formatDateTime(dateString) {
      if (!dateString) return 'N/A';
      return formatMalaysiaDateTime(dateString);
    },

    formatTimeAgo(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const seconds = Math.floor((now - date) / 1000);

      if (seconds < 60) return 'just now';
      if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
      if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
      if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
      // Format as DD/MM/YYYY for older entries
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    },

    truncate(text, length) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    },

    getEventTypeText(eventType) {
      // If already a string, return as is
      if (typeof eventType === 'string' && isNaN(eventType)) {
        return eventType;
      }

      // Convert enum number to text
      const eventTypeMap = {
        // Authentication (0-99)
        0: 'Login',
        1: 'Logout',
        2: 'Login Failed',
        3: 'Password Changed',
        4: 'Password Reset',
        5: 'Two Factor Enabled',
        6: 'Two Factor Disabled',

        // Stock Management (100-199)
        100: 'Stock Created',
        101: 'Stock Updated',
        102: 'Stock Deleted',
        103: 'Stock Viewed',
        104: 'Stock To Buy Created',
        105: 'Stock To Buy Updated',
        106: 'Stock To Buy Deleted',

        // Customer Management (200-299)
        200: 'Customer Created',
        201: 'Customer Updated',
        202: 'Customer Deleted',
        203: 'Customer Viewed',

        // Vehicle Management (300-399)
        300: 'Vehicle Created',
        301: 'Vehicle Updated',
        302: 'Vehicle Deleted',
        303: 'Vehicle Viewed',

        // Sales & Purchase (400-499)
        400: 'Sale Created',
        401: 'Sale Updated',
        402: 'Sale Deleted',
        403: 'Purchase Created',
        404: 'Purchase Updated',
        405: 'Purchase Deleted',

        // User & Role Management (500-599)
        500: 'User Created',
        501: 'User Updated',
        502: 'User Deleted',
        503: 'Role Created',
        504: 'Role Updated',
        505: 'Role Deleted',
        506: 'Role Assigned',
        507: 'Role Revoked',
        508: 'Permission Changed',

        // Master Data Management (900-999)
        900: 'Bank Created',
        901: 'Bank Updated',
        902: 'Bank Deleted',
        903: 'Brand Created',
        904: 'Brand Updated',
        905: 'Brand Deleted',
        906: 'Supplier Created',
        907: 'Supplier Updated',
        908: 'Supplier Deleted',

        // Generic
        1000: 'Entity Created',
        1001: 'Entity Updated',
        1002: 'Entity Deleted',
      };

      return eventTypeMap[eventType] || `Event ${eventType}`;
    },

    getEventTypeBadgeColor(eventType) {
      const eventText = this.getEventTypeText(eventType);
      const colors = {
        'Login': 'success',
        'Logout': 'info',
        'Login Failed': 'danger',
        'Password Changed': 'warning',
        'Password Reset': 'warning',

        // Created events
        'Stock Created': 'primary',
        'Customer Created': 'primary',
        'Vehicle Created': 'primary',
        'Bank Created': 'primary',
        'Brand Created': 'primary',
        'Supplier Created': 'primary',
        'User Created': 'primary',
        'Role Created': 'primary',
        'Entity Created': 'primary',

        // Updated events
        'Stock Updated': 'warning',
        'Customer Updated': 'warning',
        'Vehicle Updated': 'warning',
        'Bank Updated': 'warning',
        'Brand Updated': 'warning',
        'Supplier Updated': 'warning',
        'User Updated': 'warning',
        'Role Updated': 'warning',
        'Entity Updated': 'warning',

        // Deleted events
        'Stock Deleted': 'danger',
        'Customer Deleted': 'danger',
        'Vehicle Deleted': 'danger',
        'Bank Deleted': 'danger',
        'Brand Deleted': 'danger',
        'Supplier Deleted': 'danger',
        'User Deleted': 'danger',
        'Role Deleted': 'danger',
        'Entity Deleted': 'danger',

        // Role events
        'Role Assigned': 'info',
        'Role Revoked': 'warning',
        'Permission Changed': 'warning',
      };
      return colors[eventText] || 'secondary';
    },

    getSeverityBadgeColor(severity) {
      const colors = {
        'Info': 'info',
        'Low': 'success',
        'Medium': 'warning',
        'High': 'danger',
        'Critical': 'danger',
      };
      return colors[severity] || 'secondary';
    },

    getSeverityIcon(severity) {
      const icons = {
        'Info': 'fas fa-info-circle',
        'Low': 'fas fa-check-circle',
        'Medium': 'fas fa-exclamation-triangle',
        'High': 'fas fa-exclamation-circle',
        'Critical': 'fas fa-skull-crossbones',
      };
      return icons[severity] || 'fas fa-circle';
    },

    formatJSON(jsonString) {
      try {
        const parsed = JSON.parse(jsonString);
        return JSON.stringify(parsed, null, 2);
      } catch (e) {
        return jsonString;
      }
    },

    formatValue(value) {
      if (value === null || value === undefined) {
        return 'N/A';
      }
      if (typeof value === 'object') {
        return JSON.stringify(value, null, 2);
      }
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }
      // Format numbers with thousand separators if it's a large number
      if (typeof value === 'number' && value > 999) {
        return value.toLocaleString();
      }
      return value;
    },

    /**
     * Compare old and new values to detect changes
     * Returns object with field metadata: { fieldName: { type: 'added'|'removed'|'modified'|'unchanged', oldValue, newValue } }
     */
    compareValues(oldValues, newValues) {
      const comparison = {};
      const allKeys = new Set([
        ...Object.keys(oldValues || {}),
        ...Object.keys(newValues || {}),
      ]);

      allKeys.forEach((key) => {
        const hasOld = oldValues && oldValues.hasOwnProperty(key);
        const hasNew = newValues && newValues.hasOwnProperty(key);

        if (!hasOld && hasNew) {
          // Field was added
          comparison[key] = {
            type: 'added',
            oldValue: null,
            newValue: newValues[key],
          };
        } else if (hasOld && !hasNew) {
          // Field was removed
          comparison[key] = {
            type: 'removed',
            oldValue: oldValues[key],
            newValue: null,
          };
        } else if (hasOld && hasNew) {
          // Check if values are different
          const oldVal = oldValues[key];
          const newVal = newValues[key];

          // Normalize date values for proper comparison
          // This ensures date-only fields ignore time component differences
          const oldValNormalized = this.normalizeDateValue(oldVal, key);
          const newValNormalized = this.normalizeDateValue(newVal, key);

          if (JSON.stringify(oldValNormalized) !== JSON.stringify(newValNormalized)) {
            comparison[key] = {
              type: 'modified',
              oldValue: oldVal,
              newValue: newVal,
            };
          } else {
            comparison[key] = {
              type: 'unchanged',
              oldValue: oldVal,
              newValue: newVal,
            };
          }
        }
      });

      return comparison;
    },

    /**
     * Get change type for a specific field
     */
    getChangeType(key, oldValues, newValues) {
      const hasOld = oldValues && oldValues.hasOwnProperty(key);
      const hasNew = newValues && newValues.hasOwnProperty(key);

      if (!hasOld && hasNew) return 'added';
      if (hasOld && !hasNew) return 'removed';
      if (hasOld && hasNew) {
        return JSON.stringify(oldValues[key]) !== JSON.stringify(newValues[key])
          ? 'modified'
          : 'unchanged';
      }
      return 'unchanged';
    },

    /**
     * Get CSS class for change type
     */
    getChangeClass(changeType) {
      const classes = {
        added: 'field-added',
        removed: 'field-removed',
        modified: 'field-modified',
        unchanged: 'field-unchanged',
      };
      return classes[changeType] || '';
    },

    /**
     * Get icon for change type
     */
    getChangeIcon(changeType) {
      const icons = {
        added: 'fas fa-plus-circle text-success',
        removed: 'fas fa-minus-circle text-danger',
        modified: 'fas fa-edit text-warning',
        unchanged: '',
      };
      return icons[changeType] || '';
    },

    /**
     * Check if value is an array
     */
    isArray(value) {
      return Array.isArray(value);
    },

    /**
     * Check if value is a plain object (not array, not null)
     */
    isPlainObject(value) {
      return value !== null && typeof value === 'object' && !Array.isArray(value);
    },

    /**
     * Check if a datetime string represents a date-only value
     * Uses both field name patterns and midnight timestamp detection
     * Date-only fields: ExpenseDate, PaymentDate, ArrivalDate, etc.
     * Datetime fields: Timestamp, CreatedAt, UpdatedAt, etc.
     */
    isDateOnly(value, fieldName = '') {
      if (typeof value !== 'string') return false;
      if (!value.match(/^\d{4}-\d{2}-\d{2}T/)) return false;

      // Check field name for common date-only patterns
      const dateOnlyPatterns = [
        /Date$/i,           // ExpenseDate, PaymentDate, ArrivalDate, etc.
        /^Date/i,           // DateOfBirth, DateReceived, etc.
        /Birthday$/i,       // Birthday field
        /Expiry/i,          // ExpiryDate, Expiry
        /^DOB$/i,           // DOB (Date of Birth)
      ];

      // Exclude datetime field patterns
      const datetimePatterns = [
        /Timestamp$/i,      // Timestamp, UpdatedTimestamp
        /CreatedAt$/i,      // CreatedAt
        /UpdatedAt$/i,      // UpdatedAt
        /ModifiedAt$/i,     // ModifiedAt
        /DateTime$/i,       // DateTime fields
      ];

      // If field name matches datetime pattern, it's NOT date-only
      if (fieldName && datetimePatterns.some(pattern => pattern.test(fieldName))) {
        return false;
      }

      // If field name matches date-only pattern, it IS date-only
      if (fieldName && dateOnlyPatterns.some(pattern => pattern.test(fieldName))) {
        return true;
      }

      // Fallback: Check if time is midnight (00:00:00)
      const date = new Date(value);
      return date.getUTCHours() === 0 &&
             date.getUTCMinutes() === 0 &&
             date.getUTCSeconds() === 0 &&
             date.getUTCMilliseconds() === 0;
    },

    /**
     * Normalize date values for comparison
     * For date-only fields, extract just the date part (YYYY-MM-DD)
     * This ensures "2024-01-15T00:00:00" and "2024-01-15T08:00:00" are treated as the same date
     */
    normalizeDateValue(value, fieldName = '') {
      if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T/)) {
        if (this.isDateOnly(value, fieldName)) {
          // For date-only fields, compare only the date part (YYYY-MM-DD)
          return value.substring(0, 10);
        }
      }
      return value;
    },

    /**
     * Compare two arrays and return comparison data
     * Returns: { added: [], removed: [], modified: [], unchanged: [] }
     */
    compareArrays(oldArray, newArray) {
      oldArray = oldArray || [];
      newArray = newArray || [];

      const result = {
        added: [],
        removed: [],
        modified: [],
        unchanged: [],
      };

      // For simple arrays (strings, numbers), do direct comparison
      if (oldArray.length === 0) {
        result.added = newArray.map(item => ({ item, index: newArray.indexOf(item) }));
        return result;
      }

      if (newArray.length === 0) {
        result.removed = oldArray.map(item => ({ item, index: oldArray.indexOf(item) }));
        return result;
      }

      // For object arrays, try to match by a unique identifier or position
      const isObjectArray = this.isPlainObject(oldArray[0]) || this.isPlainObject(newArray[0]);

      if (isObjectArray) {
        // Create maps for efficient lookup
        const oldMap = new Map(oldArray.map((item, idx) => [this.getItemKey(item, idx), { item, idx }]));
        const newMap = new Map(newArray.map((item, idx) => [this.getItemKey(item, idx), { item, idx }]));

        // Find added and modified items
        newArray.forEach((newItem, newIdx) => {
          const key = this.getItemKey(newItem, newIdx);
          const oldEntry = oldMap.get(key);

          if (!oldEntry) {
            // Item was added
            result.added.push({ item: newItem, index: newIdx });
          } else {
            // Check if item was modified
            if (JSON.stringify(oldEntry.item) !== JSON.stringify(newItem)) {
              result.modified.push({
                oldItem: oldEntry.item,
                newItem: newItem,
                oldIndex: oldEntry.idx,
                newIndex: newIdx,
              });
            } else {
              result.unchanged.push({ item: newItem, index: newIdx });
            }
          }
        });

        // Find removed items
        oldArray.forEach((oldItem, oldIdx) => {
          const key = this.getItemKey(oldItem, oldIdx);
          if (!newMap.has(key)) {
            result.removed.push({ item: oldItem, index: oldIdx });
          }
        });
      } else {
        // Simple value comparison
        oldArray.forEach((oldItem, idx) => {
          if (!newArray.includes(oldItem)) {
            result.removed.push({ item: oldItem, index: idx });
          }
        });

        newArray.forEach((newItem, idx) => {
          if (!oldArray.includes(newItem)) {
            result.added.push({ item: newItem, index: idx });
          } else if (oldArray[idx] === newItem) {
            result.unchanged.push({ item: newItem, index: idx });
          }
        });
      }

      return result;
    },

    /**
     * Get a unique key for an array item (for comparison)
     */
    getItemKey(item, index) {
      if (this.isPlainObject(item)) {
        // Try common ID fields first
        if (item.Id) return `id-${item.Id}`;
        if (item.id) return `id-${item.id}`;
        if (item.Name) return `name-${item.Name}`;
        if (item.name) return `name-${item.name}`;
        // Fallback to JSON string + index
        return `${JSON.stringify(item)}-${index}`;
      }
      return `${item}-${index}`;
    },

    /**
     * Format value for display in comparison view
     * Handles date-only fields specially to avoid showing unnecessary time
     */
    formatDisplayValue(value, fieldName = '') {
      if (value === null || value === undefined) return 'N/A';
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (typeof value === 'number') return value.toLocaleString();
      if (typeof value === 'string') {
        // Check if it's a date/datetime string
        if (value.match(/^\d{4}-\d{2}-\d{2}T/)) {
          const date = new Date(value);
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();

          // For date-only fields, display only the date (no time)
          if (this.isDateOnly(value, fieldName)) {
            return `${day}/${month}/${year}`; // e.g., "15/01/2024"
          }

          // For datetime fields, display full date and time
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const seconds = String(date.getSeconds()).padStart(2, '0');
          return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`; // e.g., "15/01/2024 15:30:00"
        }
        return value;
      }
      if (this.isArray(value)) return `[Array: ${value.length} items]`;
      if (this.isPlainObject(value)) return '[Object]';
      return String(value);
    },
  },
};
</script>

<style scoped>
/* Professional Container */
.audit-log-wrapper {
  padding: 1.5rem;
  min-height: 100vh;
}

/* Toast Notifications */
.toast-container {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 1050;
}

/* Main Card */
.main-card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

/* Professional Header */
.professional-header {
  background: white;
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1.25rem 1.5rem;
}

.header-title h5 {
  font-size: 1.25rem;
  color: var(--cui-body-color, #212529);
  margin-bottom: 0.25rem;
}

.header-title small {
  font-size: 0.875rem;
}

.result-counter {
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.5rem 0.75rem;
}

/* Loading & Empty States */
.loading-state,
.empty-state {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-state i {
  font-size: 3rem;
  opacity: 0.5;
}

/* Professional Table */
.professional-table {
  font-size: 0.875rem;
}

.professional-table th {
  font-weight: 600;
  font-size: 0.8125rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.professional-table tbody tr {
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.professional-table tbody tr:hover {
  background-color: var(--cui-table-hover-bg, rgba(0, 0, 0, 0.075));
}

/* Footer */
.card-footer {
  background: var(--cui-body-bg, #f8f9fa);
  border-top: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1rem 1.5rem;
}

.footer-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* Enhanced Change History Comparison Styles */
.comparison-card {
  border: 1px solid var(--cui-border-color, #d8dbe0);
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.comparison-table {
  font-size: 0.875rem;
}

.comparison-table thead th {
  background-color: var(--cui-body-bg, #f8f9fa);
  font-weight: 600;
  font-size: 0.8125rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 0.75rem;
}

.comparison-table tbody tr {
  transition: all 0.15s ease;
}

/* Field Change Type Highlighting */
.field-added {
  background-color: #d4edda !important;
  border-left: 4px solid #28a745;
}

.field-removed {
  background-color: #f8d7da !important;
  border-left: 4px solid #dc3545;
}

.field-modified {
  background-color: #fff3cd !important;
  border-left: 4px solid #ffc107;
}

.field-unchanged {
  background-color: transparent;
  opacity: 0.6;
}

.change-icon-cell {
  vertical-align: middle;
  padding: 0.5rem 0.25rem;
}

.change-icon-cell i {
  font-size: 1rem;
}

.field-name-cell {
  vertical-align: middle;
  font-weight: 600;
  padding: 0.5rem 0.75rem;
}

.value-cell {
  vertical-align: middle;
  padding: 0.5rem 0.75rem;
  word-break: break-word;
}

.value-text {
  display: inline-block;
  max-width: 100%;
  word-wrap: break-word;
}

/* Array Comparison Styles */
.array-detail-row {
  background-color: var(--cui-body-bg, #f8f9fa);
}

.array-comparison-detail {
  background: white;
  border: 1px solid var(--cui-border-color, #d8dbe0);
  border-radius: 0.25rem;
  padding: 1rem;
}

.array-preview {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 0.25rem;
  font-size: 0.875rem;
}

.array-diff {
  margin-top: 0.5rem;
}

.array-items {
  margin-top: 0.5rem;
  margin-left: 1.5rem;
}

.array-item {
  border-radius: 0.25rem;
  border: 1px solid transparent;
}

.array-item.field-added {
  border-color: #28a745;
  background-color: #d4edda;
}

.array-item.field-removed {
  border-color: #dc3545;
  background-color: #f8d7da;
}

.array-item.field-modified {
  border-color: #ffc107;
  background-color: #fff3cd;
}

.array-item small {
  font-size: 0.8125rem;
  line-height: 1.5;
}

/* Toggle Switch Styling */
.form-check-input:checked {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.form-check-label {
  font-size: 0.875rem;
  color: var(--cui-body-color, #212529);
  cursor: pointer;
}

/* Hover Effects */
.comparison-table tbody tr:hover {
  opacity: 1 !important;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.1);
}

.comparison-table tbody tr.field-unchanged:hover {
  background-color: rgba(0, 0, 0, 0.02) !important;
}

/* Print Styles for Audit Logs */
@media print {
  .comparison-table {
    font-size: 10pt;
  }

  .field-added {
    border: 2px solid #28a745;
    background-color: #e7f7e9 !important;
  }

  .field-removed {
    border: 2px solid #dc3545;
    background-color: #f9e3e5 !important;
  }

  .field-modified {
    border: 2px solid #ffc107;
    background-color: #fffae6 !important;
  }

  .form-check {
    display: none;
  }
}

/* Responsive Adjustments */
@media (max-width: 767.98px) {
  .audit-log-wrapper {
    padding: 1rem;
  }

  .professional-header {
    padding: 1rem;
  }

  .header-title h5 {
    font-size: 1.125rem;
  }

  .card-footer {
    padding: 1rem;
  }

  .footer-actions {
    width: 100%;
  }

  .footer-actions .btn {
    flex: 1;
  }

  /* Responsive Table for Mobile */
  .comparison-table {
    display: block;
    overflow-x: auto;
  }

  .comparison-table thead {
    display: none;
  }

  .comparison-table tbody tr {
    display: block;
    margin-bottom: 1rem;
    border: 1px solid var(--cui-border-color, #d8dbe0);
    border-radius: 0.25rem;
  }

  .comparison-table tbody td {
    display: block;
    text-align: left !important;
    padding: 0.5rem 1rem;
    border: none;
  }

  .comparison-table tbody td:before {
    content: attr(data-label);
    font-weight: 600;
    display: block;
    margin-bottom: 0.25rem;
    font-size: 0.75rem;
    text-transform: uppercase;
    color: var(--cui-text-muted, #6c757d);
  }

  .change-icon-cell {
    display: inline-block;
    width: auto;
    margin-right: 0.5rem;
  }

  .field-name-cell:before {
    content: 'Field: ';
  }

  .value-cell:nth-child(3):before {
    content: 'Before: ';
  }

  .value-cell:nth-child(4):before {
    content: 'After: ';
  }

  .array-items {
    margin-left: 0;
  }

  .array-comparison-detail {
    padding: 0.75rem;
  }
}
</style>
